import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ImagePlus, X, Upload, User } from "lucide-react";
import ImageCropModal from "../../../shared/components/ImageCropModal";
import { useImageCrop } from "../../../shared/hooks/useImageCrop";
import { useLanguage } from "../../../context/LanguageContext";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE_MB = 5;

interface Props {
  gallery: File[];
  profileImage: File | null;
  isLoading: boolean;
  onGalleryChange: (files: File[]) => void;
  onProfileImageChange: (file: File | null) => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

function useObjectUrl(file: File | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function GalleryThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const url = useObjectUrl(file);
  if (!url) return null;
  return (
    <div className="relative aspect-square rounded-xl overflow-hidden border border-gray-200">
      <img src={url} alt="" className="w-full h-full object-cover" />
      <button type="button" onClick={onRemove}
        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80 transition-colors">
        <X size={12} />
      </button>
    </div>
  );
}

function ProfileThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const url = useObjectUrl(file);
  if (!url) return null;
  return (
    <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-[#b3d130]">
      <img src={url} alt="Profile" className="w-full h-full object-cover" />
      <button type="button" onClick={onRemove}
        className="absolute top-0 right-0 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80 transition-colors">
        <X size={12} />
      </button>
    </div>
  );
}

function validateFile(file: File, t: (key: string, params?: Record<string, string | number>) => string): string | null {
  if (!ALLOWED_TYPES.includes(file.type)) return t("auth.galleryUploadStep.unsupportedFormat", { name: file.name });
  if (file.size > MAX_SIZE_MB * 1024 * 1024) return t("auth.galleryUploadStep.exceedsSize", { name: file.name, maxMb: MAX_SIZE_MB });
  return null;
}

export default function GalleryUploadStep({ gallery, profileImage, isLoading, onGalleryChange, onProfileImageChange, onBack, onSubmit }: Props) {
  const { t } = useLanguage();
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState("");
  const dropRef = useRef<HTMLDivElement>(null);

  const galleryCrop = useImageCrop();
  const profileCrop = useImageCrop();

  const addFiles = useCallback((incoming: File[]) => {
    setFileError("");
    const errors: string[] = [];
    const valid = incoming.filter((f) => {
      const err = validateFile(f, t);
      if (err) { errors.push(err); return false; }
      return true;
    });
    if (errors.length) setFileError(errors[0]);
    const slotsLeft = 6 - gallery.length;
    const toProcess = valid.slice(0, slotsLeft);
    if (!toProcess.length) return;
    galleryCrop.openCrop(toProcess, (croppedFiles) => {
      const merged = [...gallery, ...croppedFiles].slice(0, 6);
      onGalleryChange(merged);
    });
  }, [gallery, onGalleryChange, galleryCrop, t]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleProfileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file, t);
    if (err) { setFileError(err); return; }
    profileCrop.openCrop([file], ([cropped]) => onProfileImageChange(cropped));
    e.target.value = "";
  };

  const remaining = 6 - gallery.length;

  return (
    <>
    <motion.form initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8" onSubmit={onSubmit}>
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{t("auth.galleryUploadStep.title")}</h3>
        <p className="text-sm text-gray-500">{t("auth.galleryUploadStep.subtitlePrefix")} <span className="text-red-500 font-medium">{t("auth.galleryUploadStep.required")}</span> {t("auth.galleryUploadStep.subtitleSuffix")}</p>
      </div>

      {fileError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{fileError}</div>
      )}

      {/* Profile Image */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <User size={18} className="text-[#b3d130]" /> {t("auth.galleryUploadStep.profilePhoto")}
        </h4>
        <div className="flex items-center gap-5">
          {profileImage ? (
            <ProfileThumb file={profileImage} onRemove={() => onProfileImageChange(null)} />
          ) : (
            <div className="w-24 h-24 rounded-full border-2 border-dashed border-red-300 flex items-center justify-center bg-white">
              <User size={32} className="text-gray-300" />
            </div>
          )}
          <div>
            <label className="cursor-pointer px-4 py-2 rounded-xl border-2 border-[#b3d130] text-[#6b7c1a] font-semibold text-sm hover:bg-[#b3d130]/10 transition-colors">
              {profileImage ? t("auth.galleryUploadStep.changePhoto") : t("auth.galleryUploadStep.uploadPhoto")}
              <input type="file" accept={ALLOWED_TYPES.join(",")} className="hidden" onChange={handleProfileInput} />
            </label>
            {!profileImage && (
              <p className="text-xs text-red-500 mt-1.5">{t("auth.galleryUploadStep.profilePhotoRequired")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <ImagePlus size={18} className="text-[#b3d130]" /> {t("auth.galleryUploadStep.galleryPhotos")}
          </h4>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${gallery.length >= 6 ? "bg-[#b3d130]/20 text-[#6b7c1a]" : "bg-gray-200 text-gray-500"}`}>
            {gallery.length} / 6
          </span>
        </div>

        {/* Drop zone */}
        {gallery.length < 6 && (
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`mb-4 border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 transition-colors ${
              dragOver ? "border-[#b3d130] bg-[#b3d130]/10" : "border-gray-300 bg-white"
            }`}
          >
            <Upload size={24} className="text-gray-400" />
            <p className="text-sm text-gray-500">{t("auth.galleryUploadStep.dragDropHere")}</p>
            <label className="cursor-pointer text-sm font-semibold text-[#b3d130] hover:underline">
              {t("auth.galleryUploadStep.browseFiles")}
              <input type="file" accept={ALLOWED_TYPES.join(",")} multiple className="hidden" onChange={handleInput} />
            </label>
            <p className="text-xs text-gray-400">{t("auth.galleryUploadStep.formatsHint", { maxMb: MAX_SIZE_MB })} · {remaining === 1 ? t("auth.galleryUploadStep.slotsLeftOne", { count: remaining }) : t("auth.galleryUploadStep.slotsLeft", { count: remaining })}</p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          {gallery.map((file, i) => (
            <GalleryThumb key={i} file={file} onRemove={() => onGalleryChange(gallery.filter((_, idx) => idx !== i))} />
          ))}
        </div>

        {gallery.length < 6 && (
          <p className="text-xs text-amber-600 mt-3 text-center">
            {remaining === 1 ? t("auth.galleryUploadStep.morePhotosNeededOne", { count: remaining }) : t("auth.galleryUploadStep.morePhotosNeeded", { count: remaining })}
          </p>
        )}
      </div>

      <div className="flex gap-4 pt-2">
        <button type="button" onClick={onBack} disabled={isLoading}
          className="flex-1 bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all disabled:opacity-50">
          {t("auth.back")}
        </button>
        <button type="submit" disabled={isLoading || gallery.length < 6 || !profileImage}
          className="flex-1 bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed">
          {isLoading ? t("auth.galleryUploadStep.submitting") : t("auth.galleryUploadStep.completeRegistration")}
        </button>
      </div>
    </motion.form>

    {profileCrop.session && (
      <ImageCropModal
        imageSrc={profileCrop.session.src}
        aspect={1}
        fileName={profileCrop.session.fileName}
        mimeType={profileCrop.session.mimeType}
        title={t("auth.galleryUploadStep.cropProfilePhoto")}
        onCrop={profileCrop.handleCropDone}
        onClose={profileCrop.handleCropCancel}
      />
    )}

    {galleryCrop.session && (
      <ImageCropModal
        imageSrc={galleryCrop.session.src}
        aspect={1}
        fileName={galleryCrop.session.fileName}
        mimeType={galleryCrop.session.mimeType}
        title={galleryCrop.session.queue.length > 0
          ? t("auth.galleryUploadStep.cropGalleryPhotoWithMore", { count: galleryCrop.session.queue.length })
          : t("auth.galleryUploadStep.cropGalleryPhoto")}
        onCrop={galleryCrop.handleCropDone}
        onClose={galleryCrop.handleCropCancel}
      />
    )}
    </>
  );
}
